import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} true when the next drawer open is a direct result of a live add on this
   * page, whose section morph already left the content correct — skips the extra stale-content
   * refresh in #handleDrawerOpen for that one open. Set in #handleCartLinesUpdate. */
  #justAddedFresh = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.addEventListener('pageshow', this.#handlePageShow);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * [2026-09-12] STALE-CONTENT FIX. This component has no content-refresh path of its
   * own — every other update reaches it only via a live `cart:lines-update` event fired
   * during THIS page's session (an actual add/change). It never re-syncs from the server
   * on its own, so any time the drawer's markup was already sitting stale for reasons
   * outside that one event (a bfcache-restored page, the section-renderer's own page-load
   * cache — see `sectionRenderer`'s default `cache: !Shopify.designMode`, which is TRUE on
   * a live storefront), the drawer would keep showing exactly what it showed the moment it
   * was captured or cached, no matter what the real cart holds.
   *
   * Concretely, this is the "add to cart → go to checkout → tap Back without buying →
   * cart icon shows the right count, but opening the drawer shows it empty" bug: the icon
   * (cart-icon.js) self-corrects on every restore via its own sessionStorage cart-count
   * check, but the drawer had nothing playing that role — its DOM was frozen at whatever
   * it looked like before the trip to checkout, or, on a genuine fresh reload of a cached
   * page, at whatever cart state was baked in the moment that response was generated.
   *
   * Fix: on a bfcache restore specifically (`event.persisted`), force a real network
   * re-fetch of this exact section — `{ cache: false }` is the part that matters, since a
   * cached renderSection() call would just hand back the same stale HTML — and morph it
   * in. theme-drawer.js's own `#onPageShow` already force-closes the drawer on the same
   * event for an unrelated reason (a possibly mid-animation frozen dialog); this runs
   * independently of that, so the drawer is correct underneath regardless of whether the
   * user reopens it a second later or five minutes later.
   */
  #handlePageShow = (event) => {
    if (!event.persisted) return;
    sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'full' }).catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[cart-drawer] Stale-content refresh failed:', error);
    });
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   *
   * [2026-09-12] ALSO REFRESHES ON EVERY MANUAL OPEN — the `pageshow`/`persisted` fix above
   * only catches a genuine bfcache restore, and it turned out that wasn't the only way this
   * page can be showing stale cart content by the time someone taps the icon. In particular:
   * the new direct-to-checkout flow (product-buybox.liquid) adds via a real `<form>` POST to
   * /cart/add with `return_to=/checkout` — a full navigation AWAY from this page, not a fetch.
   * Pressing Back from checkout can land back on this exact page WITHOUT firing `persisted`
   * (some browsers treat the POST→redirect chain as ineligible for bfcache even though the
   * page itself would otherwise qualify), so the one guard that used to catch this class of
   * bug entirely misses it here. Rather than chase every browser's exact bfcache heuristics
   * for every possible path into this page, this refreshes unconditionally on manual open —
   * "by hook or by crook" the founder asked for, and correctness on every open costs one
   * cheap, already-proven `cache:false` section fetch, which is a fine trade for never again
   * showing an empty drawer over a cart that actually has something in it.
   * SKIPPED only when this open was itself triggered by a live add on THIS page load
   * (`#handleCartLinesUpdate` just set `#justAddedFresh`) — that content is already correct
   * from the native section-morph the add itself performed, so re-fetching would just be a
   * redundant network call and a flicker for no reason.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    if (this.#justAddedFresh) {
      this.#justAddedFresh = false;
    } else {
      sectionRenderer.renderSection('cart-drawer-section', { cache: false, mode: 'full' }).catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Stale-content refresh failed:', error);
      });
    }

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      // This open is a direct result of a live add on this page — the section is already
      // correct from the add's own morph, so #handleDrawerOpen's stale-content refresh above
      // must not re-fetch and flicker on top of content that's already right.
      this.#justAddedFresh = true;
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#justAddedFresh = true;
            this.#themeDrawer?.open();
          }
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
